import { track } from "@/lib/analytics"
import { forwardRef, useEffect, useRef, useState, type ReactNode } from "react"
import type { Source } from "../contract"
import { useTurnStore } from "../store"
import { blank, shortDate } from "./format"
import { CloseIcon } from "./icons"
import { ResultVote } from "./ResultVote"
import { useReducedMotion } from "./hooks"
import { useAnswerSession } from "./session"

type Relied = { before: string; phrase: string; after: string }

const KIND_LABEL: Record<Source["kind"], string> = {
  case: "Judgment",
  law: "Provision as amended for the period",
  doc: "Your document",
}

/** Underline the relied phrase inside a paragraph's text (first match only). */
function marked(text: string, phrase: string | undefined): ReactNode {
  if (!phrase) return text
  const at = text.indexOf(phrase)
  if (at < 0) return text
  return (
    <>
      {text.slice(0, at)}
      <span className="ul">{phrase}</span>
      {text.slice(at + phrase.length)}
    </>
  )
}

const dateText = (d: string) => (/^\d{4}-\d{2}-\d{2}/.test(d) ? shortDate(d) : d)

/** Side panel for one source: header, status, binding note, excerpt scrolled to the cited para. */
export const SourcePanel = forwardRef<HTMLElement, { onClose: () => void }>(function SourcePanel({ onClose }, ref) {
  const open = useAnswerSession((s) => s.source)
  const src = useTurnStore((s) => (open ? s.turns[open.turn]?.sources[open.id] : undefined))
  const bodyRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const reduced = useReducedMotion()
  const [copied, setCopied] = useState(false)
  const copyTimer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(copyTimer.current), [])
  const isOpen = !!open

  useEffect(() => {
    if (!isOpen) return
    setCopied(false)
    const id = window.setTimeout(() => {
      closeRef.current?.focus({ preventScroll: true })
      const box = bodyRef.current
      const cited = box?.querySelector<HTMLElement>(".jp.cited")
      if (box && cited && typeof box.scrollTo === "function") {
        box.scrollTo({ top: Math.max(0, cited.offsetTop - 70), behavior: reduced ? "auto" : "smooth" })
      }
    }, 60)
    return () => window.clearTimeout(id)
  }, [isOpen, open?.id, open?.turn, reduced])

  const relied: Relied | null | undefined = src?.relied ?? open?.relied
  const citedN = src?.cited_para ?? null
  const paras = src?.paras ?? []
  const meta = src ? [src.court, src.date ? dateText(src.date) : ""].filter((x) => !blank(x)).join(" · ") : ""
  const status = src && !blank(src.status)
    ? `${src.status}${blank(src.status_checked) ? "" : ` · last checked ${dateText(src.status_checked)}`}`
    : ""

  const copy = async (text: string) => {
    try {
      await navigator.clipboard?.writeText(text)
      track("answer_copied", { format: "citation" })
    } catch {
      /* clipboard blocked: nothing to copy to */
    }
    setCopied(true)
    window.clearTimeout(copyTimer.current)
    copyTimer.current = window.setTimeout(() => setCopied(false), 1600)
  }

  return (
    <aside className={`srcp${isOpen ? " open" : ""}`} aria-label="Source" ref={ref} aria-hidden={!isOpen}>
      {src ? (
        <>
          <div className="sph">
            <span className="lab">{KIND_LABEL[src.kind] ?? "Source"}</span>
            <button className="ib" type="button" aria-label="Close source panel" ref={closeRef} onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
          <div className="spt">
            <h2>{src.name || src.label}</h2>
            {meta ? <p>{meta}</p> : null}
            {!blank(src.citation) ? <p className="mono">{src.citation}</p> : null}
            {status ? <span className="chip">{status}</span> : null}
            {!blank(src.binds) ? <p className="spb">{src.binds}</p> : null}
            {src.verified ? <p className="spv">✓ {blank(src.verified_label) ? "Verified" : src.verified_label}</p> : null}
          </div>
          <div
            className="spx"
            ref={bodyRef}
            role="region"
            aria-label={src.kind === "case" ? "Judgment excerpt" : src.kind === "law" ? "Section text" : "Document excerpt"}
            tabIndex={0}
          >
            {paras.length ? (
              paras.map((p) => {
                const cited = p.cited || (citedN !== null && p.n === citedN)
                return (
                  <p key={p.n} className={`jp${cited ? " cited" : ""}`} data-n={p.n}>
                    <span className="jn">{p.n}</span>
                    <span>{cited ? marked(p.text, relied?.phrase) : p.text}</span>
                  </p>
                )
              })
            ) : relied ? (
              <p className="jp cited">
                <span className="jn">{citedN ?? ""}</span>
                <span>{relied.before}<span className="ul">{relied.phrase}</span>{relied.after}</span>
              </p>
            ) : null}
            {src.doc ? (
              <p className="spe">Page {src.doc.page}, lines {src.doc.start_line} to {src.doc.end_line}</p>
            ) : null}
            {!paras.length && !relied && !src.doc ? <p className="spe">NIRA did not keep the text of this source.</p> : null}
          </div>
          {open ? <ResultVote key={`${open.turn}:${open.id}`} src={src} turnKey={open.turn} /> : null}
          <div className="spa">
            {!blank(src.citation) ? (
              <>
                <button className="btn" type="button" onClick={() => void copy(src.citation as string)}>Copy citation</button>
                {copied ? <span className="cpd" role="status">Copied</span> : null}
              </>
            ) : null}
            {!blank(src.full_text_url) ? (
              <a href={src.full_text_url as string} target="_blank" rel="noreferrer">
                {src.kind === "case" ? "Open full judgment" : "Open full text"}
              </a>
            ) : null}
            <span className="sp" />
            <button className="btn" type="button" onClick={onClose}>Close</button>
          </div>
        </>
      ) : null}
    </aside>
  )
})
